/**
 * Клиент API. Токен передаётся заголовком Authorization; 401 на запросе с
 * токеном означает, что сессия отозвана — аккаунт помечается «нужно войти».
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, message: string, code = 'error', field?: string) {
    super(message);
    this.status = status;
    this.code = code;
    if (field) this.field = field;
  }
}

/** Сеть недоступна (запрос не дошёл до сервера). */
export function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError || (e instanceof ApiError && e.status === 0);
}

type UnauthorizedHandler = (token: string) => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 30_000);
  opts.signal?.addEventListener('abort', () => ctrl.abort(), { once: true });
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: ctrl.signal,
      cache: 'no-store',
    });
  } catch (e) {
    if (ctrl.signal.aborted && !opts.signal?.aborted) throw new ApiError(0, 'Сервер не ответил вовремя', 'timeout');
    throw e;
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string; field?: string };
    if (res.status === 401 && opts.token) onUnauthorized(opts.token);
    throw new ApiError(res.status, err.error ?? `Ошибка сервера (${res.status})`, err.code ?? 'error', err.field);
  }
  return data as T;
}

export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Чтение потока событий (SSE) через fetch: так можно передать заголовок
 * авторизации. Подходит и для GET-потока обновлений, и для POST-стриминга ИИ.
 */
export async function* sse(path: string, opts: RequestOptions = {}): AsyncGenerator<SseEvent> {
  const headers: Record<string, string> = { accept: 'text/event-stream' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  const res = await fetch(`/api${path}`, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    cache: 'no-store',
  });
  if (!res.ok || !res.body) {
    let msg = `Ошибка сервера (${res.status})`;
    let code = 'error';
    try {
      const j = (await res.json()) as { error?: string; code?: string };
      msg = j.error ?? msg;
      code = j.code ?? code;
    } catch {
      /* тело не JSON */
    }
    if (res.status === 401 && opts.token) onUnauthorized(opts.token);
    throw new ApiError(res.status, msg, code);
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  let event = 'message';
  let data: string[] = [];
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += value;
      let idx: number;
      while ((idx = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, idx).replace(/\r$/, '');
        buf = buf.slice(idx + 1);
        if (line === '') {
          if (data.length) yield { event, data: data.join('\n') };
          event = 'message';
          data = [];
        } else if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
      }
    }
  } finally {
    reader.releaseLock();
  }
}
